import {
  Car,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Gauge,
  MessageSquare,
  Truck,
  Wrench,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  fixedServices,
  HOURLY_DEFAULT,
  HOURLY_MAX,
  HOURLY_MIN,
  HOURLY_STEP,
  hourlyServices,
  pricingNotes,
  tirePriceTables,
} from '@/content/et/pricing';
import { contact, formatFullAddress } from '@/content/et/contact';
import { site } from '@/content/et/site';
import type {
  CalculatorMode,
  HourlyServiceId,
  RimType,
  VehicleClass,
} from '@/content/types';
import { formatEur } from '@/lib/format';
import {
  listTireSizeOptions,
  quoteHourly,
  quoteTireChange,
  shouldHideTireSize,
  snapHourlyHours,
  TIRE_SIZE_UI_MAX,
  TIRE_SIZE_UI_MIN,
} from '@/lib/pricingEngine';
import { Button } from '@/components/ui/Button';
import { Price } from '@/components/ui/Price';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import styles from './PricingCalculator.module.css';

const DEFAULT_SIZE = 16;
const DESKTOP_MQ = '(min-width: 768px)';

type State = {
  mode: CalculatorMode;
  vehicleClass: VehicleClass;
  rimType: RimType;
  sizeInches: number | null;
  hourlyServiceId: HourlyServiceId;
  hours: number;
  fixedServiceId: string | null;
};

type Action =
  | { type: 'SET_MODE'; mode: CalculatorMode }
  | { type: 'SET_VEHICLE'; vehicleClass: VehicleClass }
  | { type: 'SET_RIM'; rimType: RimType }
  | { type: 'SET_SIZE'; sizeInches: number }
  | { type: 'SET_HOURLY_SERVICE'; id: HourlyServiceId }
  | { type: 'SET_HOURS'; hours: number }
  | { type: 'SET_FIXED'; id: string };

function parseMode(raw: string | null): CalculatorMode {
  if (raw === 'hourly' || raw === 'fixed' || raw === 'tire') return raw;
  return 'tire';
}

function pickSize(
  vehicleClass: VehicleClass,
  rimType: RimType,
  preferred = DEFAULT_SIZE,
): number | null {
  if (shouldHideTireSize(vehicleClass, rimType)) return null;
  const opts = listTireSizeOptions(vehicleClass, rimType, tirePriceTables);
  if (!opts.length) return null;
  if (opts.includes(preferred)) return preferred;
  const min = opts[0]!;
  const max = opts[opts.length - 1]!;
  const clamped = Math.min(max, Math.max(min, preferred));
  return opts.includes(clamped)
    ? clamped
    : opts.reduce((best, n) =>
        Math.abs(n - preferred) < Math.abs(best - preferred) ? n : best,
      );
}

const initialState: State = {
  mode: 'tire',
  vehicleClass: 'passenger',
  rimType: 'steel',
  sizeInches: DEFAULT_SIZE,
  hourlyServiceId: 'remont',
  hours: HOURLY_DEFAULT,
  fixedServiceId: fixedServices[0]?.id ?? null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.mode };
    case 'SET_VEHICLE': {
      const preferred = state.sizeInches ?? DEFAULT_SIZE;
      return {
        ...state,
        vehicleClass: action.vehicleClass,
        sizeInches: pickSize(action.vehicleClass, state.rimType, preferred),
      };
    }
    case 'SET_RIM': {
      const preferred = state.sizeInches ?? DEFAULT_SIZE;
      return {
        ...state,
        rimType: action.rimType,
        sizeInches: pickSize(state.vehicleClass, action.rimType, preferred),
      };
    }
    case 'SET_SIZE':
      return { ...state, sizeInches: action.sizeInches };
    case 'SET_HOURLY_SERVICE':
      return { ...state, hourlyServiceId: action.id };
    case 'SET_HOURS':
      return { ...state, hours: snapHourlyHours(action.hours) };
    case 'SET_FIXED': {
      const svc = fixedServices.find((s) => s.id === action.id);
      if (svc?.switchToTire) {
        return {
          ...state,
          mode: 'tire',
          vehicleClass: 'passenger',
          rimType: 'steel',
          sizeInches: DEFAULT_SIZE,
          fixedServiceId: action.id,
        };
      }
      return { ...state, fixedServiceId: action.id };
    }
    default:
      return state;
  }
}

/** Live summary under the price (UI) */
function buildSummaryLine(state: State, hideSize: boolean): string {
  if (state.mode === 'tire') {
    const parts = [
      'Rehvivahetus',
      state.vehicleClass === 'passenger' ? 'sõiduauto' : 'kaubik/maastur',
      state.rimType === 'steel' ? 'plekkvelg' : 'valuvelg',
    ];
    if (!hideSize && state.sizeInches) parts.push(`${state.sizeInches}″`);
    return parts.join(' · ');
  }
  if (state.mode === 'hourly') {
    const svc = hourlyServices.find((s) => s.id === state.hourlyServiceId);
    return `${svc?.title ?? 'Töö'} · ${state.hours} h · ${svc?.rateEur ?? '—'}€/h`;
  }
  const fixed = fixedServices.find((s) => s.id === state.fixedServiceId);
  return fixed?.title ?? site.calculator.fixed;
}

/** Service line for Kopeeri paste only (no hourly rate — quote carries the €) */
function buildCopyServiceLine(state: State, hideSize: boolean): string {
  if (state.mode === 'tire') {
    return buildSummaryLine(state, hideSize);
  }
  if (state.mode === 'hourly') {
    const svc = hourlyServices.find((s) => s.id === state.hourlyServiceId);
    return `${svc?.title ?? 'Töö'} · ${state.hours} h`;
  }
  const fixed = fixedServices.find((s) => s.id === state.fixedServiceId);
  return fixed?.title ?? site.calculator.fixed;
}

/** Clipboard / form-prefill body — matches workshop-ready paste format */
function buildFullSummary(
  state: State,
  quote: number,
  hideSize: boolean,
): string {
  const serviceLine = buildCopyServiceLine(state, hideSize);
  const siteUrl = site.canonical.replace(/\/$/, '') || 'https://lõdvik.ee';

  return [
    'Lõdvik OÜ —',
    `${serviceLine},`,
    `orienteeruv hind: ${formatEur(quote)}`,
    '',
    pricingNotes.pricesIncludeVat,
    '(Lõplik summa kinnitatakse pärast ülevaatust, sõltuvalt vea ulatusest ja töö mahust.)',
    '',
    siteUrl,
    formatFullAddress(),
    contact.phoneDisplay,
  ].join('\n');
}

const moreInfoItems = [
  pricingNotes.pricesIncludeVat,
  pricingNotes.customerPartsNoWarranty,
  pricingNotes.weldingOffered,
  pricingNotes.tireUnitFourWheels,
  pricingNotes.estimateOrientation,
  pricingNotes.inclusionsSoft,
];

export type PricingCalculatorProps = {
  /** When true, render as slide-in panel body (no outer section chrome) */
  panel?: boolean;
  onClose?: () => void;
  /** Controlled mode from parent (service click) */
  externalMode?: CalculatorMode;
  externalHourlyServiceId?: HourlyServiceId;
  externalFixedServiceId?: string;
};

export function PricingCalculator({
  panel = false,
  onClose,
  externalMode,
  externalHourlyServiceId,
  externalFixedServiceId,
}: PricingCalculatorProps = {}) {
  const [searchParams] = useSearchParams();
  const [state, dispatch] = useReducer(reducer, initialState, (base) => ({
    ...base,
    mode: externalMode ?? parseMode(searchParams.get('mode')),
    sizeInches: DEFAULT_SIZE,
    hourlyServiceId: externalHourlyServiceId ?? base.hourlyServiceId,
    fixedServiceId: externalFixedServiceId ?? base.fixedServiceId,
  }));
  const [copied, setCopied] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const infoBtnRef = useRef<HTMLButtonElement>(null);
  const infoBubbleRef = useRef<HTMLDivElement>(null);
  const [infoPlacement, setInfoPlacement] = useState<{
    top: number;
    left: number;
    place: 'above' | 'below';
  } | null>(null);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const placeInfoBubble = useCallback(() => {
    const btn = infoBtnRef.current;
    if (!btn || !infoOpen || !isDesktop) {
      setInfoPlacement(null);
      return;
    }
    const r = btn.getBoundingClientRect();
    const gap = 10;
    const maxW = Math.min(320, window.innerWidth - 24);
    const bubbleH = infoBubbleRef.current?.offsetHeight ?? 240;
    const spaceAbove = r.top - gap;
    const spaceBelow = window.innerHeight - r.bottom - gap;
    const place: 'above' | 'below' =
      spaceAbove >= bubbleH + 8
        ? 'above'
        : spaceBelow >= bubbleH + 8
          ? 'below'
          : spaceAbove >= spaceBelow
            ? 'above'
            : 'below';

    let left = r.left + r.width / 2;
    const half = maxW / 2;
    left = Math.min(
      window.innerWidth - half - 12,
      Math.max(half + 12, left),
    );

    setInfoPlacement({
      top: place === 'above' ? r.top - gap : r.bottom + gap,
      left,
      place,
    });
  }, [infoOpen, isDesktop]);

  useLayoutEffect(() => {
    if (!infoOpen || !isDesktop) {
      setInfoPlacement(null);
      return;
    }
    placeInfoBubble();
    const id = requestAnimationFrame(() => placeInfoBubble());
    return () => cancelAnimationFrame(id);
  }, [infoOpen, isDesktop, placeInfoBubble]);

  useEffect(() => {
    if (!infoOpen || !isDesktop) return;
    const onReposition = () => placeInfoBubble();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [infoOpen, isDesktop, placeInfoBubble]);

  useEffect(() => {
    if (!infoOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (infoBtnRef.current?.contains(t)) return;
      if (infoBubbleRef.current?.contains(t)) return;
      setInfoOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [infoOpen]);

  useEffect(() => {
    if (externalMode) {
      dispatch({ type: 'SET_MODE', mode: externalMode });
    } else {
      dispatch({ type: 'SET_MODE', mode: parseMode(searchParams.get('mode')) });
    }
  }, [searchParams, externalMode]);

  useEffect(() => {
    if (externalHourlyServiceId) {
      dispatch({
        type: 'SET_HOURLY_SERVICE',
        id: externalHourlyServiceId,
      });
    }
  }, [externalHourlyServiceId]);

  useEffect(() => {
    if (externalFixedServiceId) {
      dispatch({ type: 'SET_FIXED', id: externalFixedServiceId });
    }
  }, [externalFixedServiceId]);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);

  const hideSize = shouldHideTireSize(state.vehicleClass, state.rimType);
  const sizeOptions = useMemo(
    () =>
      listTireSizeOptions(state.vehicleClass, state.rimType, tirePriceTables),
    [state.vehicleClass, state.rimType],
  );

  const sizeMin = sizeOptions[0] ?? TIRE_SIZE_UI_MIN;
  const sizeMax = sizeOptions[sizeOptions.length - 1] ?? TIRE_SIZE_UI_MAX;
  const currentSize = state.sizeInches ?? DEFAULT_SIZE;

  const quote = useMemo(() => {
    if (state.mode === 'tire') {
      return quoteTireChange(
        {
          vehicleClass: state.vehicleClass,
          rimType: state.rimType,
          sizeInches: state.sizeInches ?? undefined,
        },
        tirePriceTables,
      );
    }
    if (state.mode === 'hourly') {
      const svc = hourlyServices.find((s) => s.id === state.hourlyServiceId);
      if (!svc) return null;
      return quoteHourly(state.hours, svc.rateEur);
    }
    const fixed = fixedServices.find((s) => s.id === state.fixedServiceId);
    if (!fixed || fixed.switchToTire) return null;
    return fixed.priceEur;
  }, [state]);

  const summaryLine = useMemo(
    () => buildSummaryLine(state, hideSize),
    [state, hideSize],
  );

  const copySummary = useCallback(async () => {
    if (quote == null) return;
    const summary = buildFullSummary(state, quote, hideSize);
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
    } catch {
      /* ignore */
    }
  }, [quote, state, hideSize]);

  const modeOptions = [
    { value: 'tire' as const, label: site.calculator.tire },
    { value: 'hourly' as const, label: site.calculator.hourly },
    { value: 'fixed' as const, label: site.calculator.fixed },
  ];

  const stepSize = (delta: number) => {
    if (!sizeOptions.length) return;
    const idx = sizeOptions.indexOf(currentSize);
    const base = idx >= 0 ? idx : sizeOptions.indexOf(DEFAULT_SIZE);
    const next =
      sizeOptions[
        Math.min(
          sizeOptions.length - 1,
          Math.max(0, (base < 0 ? 0 : base) + delta),
        )
      ];
    if (next != null) dispatch({ type: 'SET_SIZE', sizeInches: next });
  };

  const consoleInner = (
        <div className={styles.console}>
          {panel && onClose ? (
            <header className={styles.panelHeader}>
              <div>
                <p className={styles.panelKicker}>{site.calculator.title}</p>
                <h2 id="hinnakiri-title" className={styles.panelTitle}>
                  {site.cta.calculate}
                </h2>
              </div>
              <button
                type="button"
                className={styles.panelClose}
                onClick={onClose}
                aria-label="Sulge kalkulaator"
              >
                <span aria-hidden>✕</span>
              </button>
            </header>
          ) : null}
          <div className={styles.layout}>
            {/* Estimate first in DOM for mobile sticky / visual priority */}
            <div className={`${styles.panel} ${styles.estimate}`}>
              <div className={styles.estimateHeading}>
                <span className={styles.estimateLabel}>
                  {site.calculator.estimate}
                </span>
                <span className={styles.estimateTag}>
                  {site.calculator.estimateDependsTag}
                </span>
              </div>

              <div
                className={styles.estimatePrice}
                aria-live="polite"
                aria-atomic="true"
              >
                <Price amount={quote} size="lg" className={styles.quotePrice} />
                <p
                  className={[
                    styles.empty,
                    quote == null ? '' : styles.emptyHidden,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {site.calculator.empty}
                </p>
              </div>

              <p className={styles.summaryLine} aria-live="polite">
                {summaryLine}
              </p>

              <div className={styles.moreInfo}>
                <button
                  ref={infoBtnRef}
                  type="button"
                  className={styles.moreInfoToggle}
                  aria-expanded={infoOpen}
                  aria-controls="calculator-more-info"
                  onClick={() => setInfoOpen((v) => !v)}
                >
                  {infoOpen
                    ? site.calculator.hideInfo
                    : site.calculator.moreInfo}
                  {infoOpen ? (
                    <ChevronUp size={16} strokeWidth={1.75} aria-hidden />
                  ) : (
                    <ChevronDown size={16} strokeWidth={1.75} aria-hidden />
                  )}
                </button>
                {/* Mobile: inline expand (no overflow issue) */}
                {infoOpen && !isDesktop ? (
                  <div
                    id="calculator-more-info"
                    className={styles.moreInfoBubble}
                    role="region"
                    aria-label={site.calculator.moreInfo}
                  >
                    <ul className={styles.moreInfoList}>
                      {moreInfoItems.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {/* Desktop: portal + fixed so parents cannot clip it */}
                {infoOpen &&
                isDesktop &&
                infoPlacement &&
                typeof document !== 'undefined'
                  ? createPortal(
                      <div
                        ref={infoBubbleRef}
                        id="calculator-more-info"
                        className={[
                          styles.moreInfoBubbleFixed,
                          infoPlacement.place === 'above'
                            ? styles.moreInfoBubbleAbove
                            : styles.moreInfoBubbleBelow,
                        ].join(' ')}
                        style={{
                          top: infoPlacement.top,
                          left: infoPlacement.left,
                        }}
                        role="region"
                        aria-label={site.calculator.moreInfo}
                      >
                        <ul className={styles.moreInfoList}>
                          {moreInfoItems.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>,
                      document.body,
                    )
                  : null}
              </div>
            </div>

            <div className={`${styles.panel} ${styles.controls}`}>
              <div className={styles.tabs}>
                <SegmentedControl
                  dense
                  ariaLabel="Kalkulaatori režiim"
                  options={modeOptions}
                  value={state.mode}
                  onChange={(mode) => dispatch({ type: 'SET_MODE', mode })}
                />
              </div>

              <div className={styles.fields}>
                <div
                  className={[
                    styles.modePane,
                    state.mode === 'tire' ? styles.modePaneActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-hidden={state.mode !== 'tire'}
                >
                  <SegmentedControl
                    dense
                    ariaLabel={site.calculator.vehicle}
                    options={[
                      {
                        value: 'passenger' as const,
                        label: site.calculator.passenger,
                        icon: <Car size={18} strokeWidth={2} aria-hidden />,
                      },
                      {
                        value: 'vanSuv' as const,
                        label: site.calculator.vanSuv,
                        icon: <Truck size={18} strokeWidth={2} aria-hidden />,
                      },
                    ]}
                    value={state.vehicleClass}
                    onChange={(vehicleClass) =>
                      dispatch({ type: 'SET_VEHICLE', vehicleClass })
                    }
                  />
                  <SegmentedControl
                    dense
                    ariaLabel={site.calculator.rim}
                    options={[
                      {
                        value: 'steel' as const,
                        label: site.calculator.steel,
                      },
                      {
                        value: 'alloy' as const,
                        label: site.calculator.alloy,
                      },
                    ]}
                    value={state.rimType}
                    onChange={(rimType) =>
                      dispatch({ type: 'SET_RIM', rimType })
                    }
                  />
                  <div className={styles.sizeSlot}>
                    {hideSize ? (
                      <p className={styles.flatNote}>
                        {site.calculator.steelFlatSize}
                      </p>
                    ) : (
                      <div className={styles.fieldBlock}>
                        <span
                          className={styles.fieldLabel}
                          id="tire-size-label"
                        >
                          {site.calculator.size}
                        </span>
                        <div
                          className={styles.stepper}
                          role="group"
                          aria-labelledby="tire-size-label"
                        >
                          <button
                            type="button"
                            className={styles.stepperBtn}
                            aria-label="Vähenda suurust"
                            disabled={currentSize <= sizeMin}
                            tabIndex={state.mode === 'tire' ? 0 : -1}
                            onClick={() => stepSize(-1)}
                          >
                            <ChevronLeft
                              size={28}
                              strokeWidth={1.75}
                              aria-hidden
                            />
                          </button>
                          <div
                            className={styles.stepperValue}
                            aria-live="polite"
                          >
                            <span className={styles.stepperNum}>
                              {currentSize}
                            </span>
                            <span className={styles.stepperUnit}>″</span>
                          </div>
                          <button
                            type="button"
                            className={styles.stepperBtn}
                            aria-label="Suurenda suurust"
                            disabled={currentSize >= sizeMax}
                            tabIndex={state.mode === 'tire' ? 0 : -1}
                            onClick={() => stepSize(1)}
                          >
                            <ChevronRight
                              size={28}
                              strokeWidth={1.75}
                              aria-hidden
                            />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className={[
                    styles.modePane,
                    state.mode === 'hourly' ? styles.modePaneActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-hidden={state.mode !== 'hourly'}
                >
                  <div className={styles.fieldBlock}>
                    <span
                      className={styles.fieldLabel}
                      id="hourly-service-label"
                    >
                      {site.calculator.service}
                    </span>
                    <SegmentedControl
                      dense
                      ariaLabel={site.calculator.service}
                      options={hourlyServices.map((s) => ({
                        value: s.id,
                        label: `${s.title} · ${s.rateEur}€/h`,
                        icon:
                          s.id === 'remont' ? (
                            <Wrench size={16} strokeWidth={1.75} />
                          ) : (
                            <Gauge size={16} strokeWidth={1.75} />
                          ),
                      }))}
                      value={state.hourlyServiceId}
                      onChange={(id) =>
                        dispatch({
                          type: 'SET_HOURLY_SERVICE',
                          id: id as HourlyServiceId,
                        })
                      }
                    />
                  </div>

                  <div className={styles.fieldBlock}>
                    <span className={styles.fieldLabel} id="hourly-hours-label">
                      {site.calculator.hours}
                    </span>
                    <div
                      className={styles.stepper}
                      role="group"
                      aria-labelledby="hourly-hours-label"
                    >
                      <button
                        type="button"
                        className={styles.stepperBtn}
                        aria-label="Vähenda tunde"
                        disabled={state.hours <= HOURLY_MIN}
                        tabIndex={state.mode === 'hourly' ? 0 : -1}
                        onClick={() =>
                          dispatch({
                            type: 'SET_HOURS',
                            hours: state.hours - HOURLY_STEP,
                          })
                        }
                      >
                        <ChevronLeft size={28} strokeWidth={1.75} aria-hidden />
                      </button>
                      <div className={styles.stepperValue} aria-live="polite">
                        <span className={styles.stepperNum}>{state.hours}</span>
                        <span className={styles.stepperUnit}>h</span>
                      </div>
                      <button
                        type="button"
                        className={styles.stepperBtn}
                        aria-label="Suurenda tunde"
                        disabled={state.hours >= HOURLY_MAX}
                        tabIndex={state.mode === 'hourly' ? 0 : -1}
                        onClick={() =>
                          dispatch({
                            type: 'SET_HOURS',
                            hours: state.hours + HOURLY_STEP,
                          })
                        }
                      >
                        <ChevronRight
                          size={28}
                          strokeWidth={1.75}
                          aria-hidden
                        />
                      </button>
                    </div>
                    <p className={styles.fieldHint}>
                      Alates {HOURLY_MIN} h · samm {HOURLY_STEP} h
                    </p>
                  </div>
                </div>

                <div
                  className={[
                    styles.modePane,
                    state.mode === 'fixed' ? styles.modePaneActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-hidden={state.mode !== 'fixed'}
                >
                  <div
                    className={styles.fixedList}
                    role="listbox"
                    aria-label={site.calculator.fixed}
                  >
                    {fixedServices.map((svc) => {
                      const active = state.fixedServiceId === svc.id;
                      return (
                        <button
                          key={svc.id}
                          type="button"
                          role="option"
                          aria-selected={active}
                          tabIndex={state.mode === 'fixed' ? 0 : -1}
                          className={[
                            styles.fixedRow,
                            active ? styles.fixedRowActive : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() =>
                            dispatch({ type: 'SET_FIXED', id: svc.id })
                          }
                        >
                          <span className={styles.fixedTitle}>{svc.title}</span>
                          <span className={styles.fixedPrice}>
                            {svc.isFrom
                              ? `${site.calculator.from} ${svc.priceEur}€`
                              : `${svc.priceEur}€`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* CTAs sit under the last control block */}
              <div className={styles.actions}>
                <Button
                  href="/#kontakt"
                  variant="primary"
                  fullWidth
                  className={styles.actionBtn}
                >
                  <MessageSquare size={18} strokeWidth={1.75} aria-hidden />
                  {site.nav.contact}
                </Button>
                <button
                  type="button"
                  className={[
                    styles.copyBtn,
                    copied ? styles.copyBtnDone : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => void copySummary()}
                  disabled={quote == null}
                  aria-live="polite"
                  aria-label={
                    copied ? site.calculator.copied : site.calculator.copyShort
                  }
                >
                  <span className={styles.copyViewport}>
                    <span
                      className={[
                        styles.copyTicker,
                        copied ? styles.copyTickerShift : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <span className={styles.copyLine}>
                        {site.calculator.copyShort}
                      </span>
                      <span className={styles.copyLine}>
                        <Check
                          size={16}
                          strokeWidth={2}
                          className={styles.copyIcon}
                          aria-hidden
                        />
                        {site.calculator.copied}
                      </span>
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
  );

  if (panel) {
    return (
      <div className={styles.panelRoot} id="hinnakiri">
        {consoleInner}
      </div>
    );
  }

  return (
    <section
      id="hinnakiri"
      className={`section ${styles.section}`}
      aria-labelledby="hinnakiri-title"
    >
      <div className="container">
        <header className={styles.header}>
          <div>
            <h2
              id="hinnakiri-title"
              className="section__title section__title--tight"
            >
              {site.calculator.title}
            </h2>
            <p className={styles.lead}>{site.calculator.lead}</p>
          </div>
        </header>
        {consoleInner}
      </div>
    </section>
  );
}
